import { useState, useEffect, useCallback, useRef } from 'react';
import { Text } from '@qwickapps/react-framework';
import { config } from '../../config/runtime';

interface Goal {
  id: string;
  title: string;
  status: string;
  description?: string;
}

interface Item {
  id: string;
  title: string;
  status: 'open' | 'in_progress' | 'blocked' | 'done' | 'cancelled';
  assignee?: string;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  notes?: string;
}

interface EditItemForm {
  title: string;
  status: Item['status'];
  assignee: string;
  priority: string;
  notes: string;
}

interface EditGoalForm {
  title: string;
  description: string;
  status: string;
}

const REFRESH_INTERVAL_MS = 30_000;

const STATUS_COLORS: Record<string, string> = {
  open: '#9e9e9e',
  in_progress: '#2196f3',
  blocked: '#f44336',
  done: '#4caf50',
  cancelled: '#757575',
};

const PRIORITY_COLORS: Record<string, string> = {
  low: '#9e9e9e',
  medium: '#ff9800',
  high: '#f44336',
  critical: '#b71c1c',
};

const ITEM_STATUS_OPTIONS: Item['status'][] = ['open', 'in_progress', 'blocked', 'done', 'cancelled'];
const ITEM_PRIORITY_OPTIONS = ['', 'low', 'medium', 'high', 'critical'];
const GOAL_STATUS_OPTIONS = ['active', 'blocked', 'done', 'cancelled'];

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  borderRadius: 4,
  border: '1px solid var(--theme-border-color)',
  background: 'var(--theme-bg-secondary)',
  color: 'var(--theme-text-primary)',
  fontSize: 13,
  boxSizing: 'border-box',
  outline: 'none',
};

const btnBase: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '2px 6px',
  borderRadius: 4,
  fontSize: 13,
  lineHeight: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export default function GoalsItemsPanel() {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [itemsByGoal, setItemsByGoal] = useState<Record<string, Item[]>>({});
  const [expandedGoals, setExpandedGoals] = useState<Set<string>>(new Set());
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // --- Item edit dialog state ---
  const [isItemEditOpen, setIsItemEditOpen] = useState(false);
  const [itemEditForm, setItemEditForm] = useState<EditItemForm>({
    title: '',
    status: 'open',
    assignee: '',
    priority: '',
    notes: '',
  });
  const [isItemSaving, setIsItemSaving] = useState(false);
  const [itemSaveError, setItemSaveError] = useState<string | null>(null);

  // --- Item archive state ---
  const [isArchivingItem, setIsArchivingItem] = useState(false);

  // --- Goal edit dialog state ---
  const [isGoalEditOpen, setIsGoalEditOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [goalEditForm, setGoalEditForm] = useState<EditGoalForm>({
    title: '',
    description: '',
    status: 'active',
  });
  const [isGoalSaving, setIsGoalSaving] = useState(false);
  const [goalSaveError, setGoalSaveError] = useState<string | null>(null);

  // --- Goal archive confirm state ---
  const [isGoalArchiveOpen, setIsGoalArchiveOpen] = useState(false);
  const [goalArchiveStatus, setGoalArchiveStatus] = useState<'done' | 'cancelled'>('done');
  const [isGoalArchiving, setIsGoalArchiving] = useState(false);

  const isMounted = useRef(true);

  const fetchData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setIsRefreshing(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/projects/goals?status=active`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: unknown = await res.json();
      if (!isMounted.current) return;
      const fetchedGoals: Goal[] = Array.isArray(data)
        ? (data as Goal[])
        : ((data as Record<string, unknown>).goals as Goal[]) ?? [];
      setGoals(fetchedGoals);
      setError(null);

      const itemMap: Record<string, Item[]> = {};
      await Promise.all(
        fetchedGoals.map(async (goal) => {
          try {
            const iRes = await fetch(`${config.apiUrl}/api/projects/items?goal_id=${goal.id}`);
            if (!iRes.ok) return;
            const iData: unknown = await iRes.json();
            const items: Item[] = Array.isArray(iData)
              ? (iData as Item[])
              : ((iData as Record<string, unknown>).items as Item[]) ?? [];
            itemMap[goal.id] = items;
          } catch {
            itemMap[goal.id] = [];
          }
        }),
      );
      if (!isMounted.current) return;
      setItemsByGoal(itemMap);
    } catch {
      if (!isMounted.current) return;
      setError('Unable to fetch goals');
    } finally {
      if (isMounted.current) {
        setLoading(false);
        if (showRefreshing) setIsRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    fetchData(false);
    const interval = setInterval(() => fetchData(true), REFRESH_INTERVAL_MS);
    return () => {
      isMounted.current = false;
      clearInterval(interval);
    };
  }, [fetchData]);

  const toggleGoal = (goalId: string) => {
    setExpandedGoals((prev) => {
      const next = new Set(prev);
      if (next.has(goalId)) {
        next.delete(goalId);
      } else {
        next.add(goalId);
      }
      return next;
    });
  };

  // ── Item edit handlers ──────────────────────────────────────────────────────

  const openItemEditDialog = () => {
    if (!selectedItem) return;
    setItemEditForm({
      title: selectedItem.title,
      status: selectedItem.status,
      assignee: selectedItem.assignee ?? '',
      priority: selectedItem.priority ?? '',
      notes: selectedItem.notes ?? '',
    });
    setItemSaveError(null);
    setIsItemEditOpen(true);
  };

  const closeItemEditDialog = () => {
    setIsItemEditOpen(false);
    setItemSaveError(null);
  };

  const handleItemEditSave = async () => {
    if (!selectedItem) return;
    const title = itemEditForm.title.trim();
    if (!title) {
      setItemSaveError('Title is required.');
      return;
    }
    setIsItemSaving(true);
    setItemSaveError(null);
    try {
      const body: Record<string, unknown> = { title, status: itemEditForm.status };
      if (itemEditForm.assignee.trim()) body.assignee = itemEditForm.assignee.trim();
      if (itemEditForm.priority) body.priority = itemEditForm.priority;
      if (itemEditForm.notes.trim()) body.notes = itemEditForm.notes.trim();

      const res = await fetch(`${config.apiUrl}/api/projects/items/${selectedItem.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = (await res.json()) as Item;

      setItemsByGoal((prev) => {
        const next = { ...prev };
        for (const goalId of Object.keys(next)) {
          next[goalId] = next[goalId].map((it) =>
            it.id === selectedItem.id ? { ...it, ...updated } : it,
          );
        }
        return next;
      });
      setSelectedItem((prev) => (prev ? { ...prev, ...updated } : prev));
      setIsItemEditOpen(false);
    } catch {
      setItemSaveError('Failed to save changes. Please try again.');
    } finally {
      setIsItemSaving(false);
    }
  };

  // ── Item archive handler ────────────────────────────────────────────────────

  const handleArchiveItem = async () => {
    if (!selectedItem) return;
    setIsArchivingItem(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/projects/items/${selectedItem.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'done' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = (await res.json()) as Item;

      setItemsByGoal((prev) => {
        const next = { ...prev };
        for (const goalId of Object.keys(next)) {
          next[goalId] = next[goalId].map((it) =>
            it.id === selectedItem.id ? { ...it, ...updated } : it,
          );
        }
        return next;
      });
      setSelectedItem(null);
    } catch {
      // silently fail — user can retry via edit
    } finally {
      setIsArchivingItem(false);
    }
  };

  // ── Goal edit handlers ──────────────────────────────────────────────────────

  const openGoalEditDialog = (goal: Goal, e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setSelectedGoal(goal);
    setGoalEditForm({
      title: goal.title,
      description: goal.description ?? '',
      status: goal.status,
    });
    setGoalSaveError(null);
    setIsGoalEditOpen(true);
  };

  const closeGoalEditDialog = () => {
    setIsGoalEditOpen(false);
    setGoalSaveError(null);
  };

  const handleGoalEditSave = async () => {
    if (!selectedGoal) return;
    const title = goalEditForm.title.trim();
    if (!title) {
      setGoalSaveError('Title is required.');
      return;
    }
    setIsGoalSaving(true);
    setGoalSaveError(null);
    try {
      const body: Record<string, unknown> = { title, status: goalEditForm.status };
      if (goalEditForm.description.trim()) body.description = goalEditForm.description.trim();

      const res = await fetch(`${config.apiUrl}/api/projects/goals/${selectedGoal.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { goal: Goal } | Goal;
      const updated: Goal = 'goal' in data ? (data as { goal: Goal }).goal : (data as Goal);

      setGoals((prev) =>
        prev.map((g) => (g.id === selectedGoal.id ? { ...g, ...updated } : g)),
      );
      setIsGoalEditOpen(false);
    } catch {
      setGoalSaveError('Failed to save changes. Please try again.');
    } finally {
      setIsGoalSaving(false);
    }
  };

  // ── Goal archive handlers ───────────────────────────────────────────────────

  const openGoalArchiveDialog = (goal: Goal, e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setSelectedGoal(goal);
    setGoalArchiveStatus('done');
    setIsGoalArchiveOpen(true);
  };

  const closeGoalArchiveDialog = () => {
    setIsGoalArchiveOpen(false);
  };

  const handleGoalArchive = async () => {
    if (!selectedGoal) return;
    setIsGoalArchiving(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/projects/goals/${selectedGoal.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: goalArchiveStatus }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // Remove the archived goal from active goals list
      setGoals((prev) => prev.filter((g) => g.id !== selectedGoal.id));
      setIsGoalArchiveOpen(false);
    } catch {
      // silently fail
    } finally {
      setIsGoalArchiving(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <Text variant="h5" content="Goals & Items" gutterBottom />
        {isRefreshing && (
          <div
            role="status"
            aria-label="refreshing"
            style={{
              width: 12,
              height: 12,
              border: '2px solid var(--theme-border-color)',
              borderTopColor: 'var(--theme-color-primary)',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }}
          />
        )}
      </div>

      {loading && <Text variant="body2" content="Loading..." customColor="var(--theme-text-secondary)" />}

      {error && (
        <div
          style={{
            padding: 12,
            borderRadius: 4,
            background: 'rgba(244,67,54,0.08)',
            border: '1px solid #f44336',
            marginBottom: 16,
          }}
        >
          <Text variant="body2" content={error} customColor="#f44336" />
        </div>
      )}

      {!loading && !error && goals.length === 0 && (
        <Text variant="body2" content="No active goals found." customColor="var(--theme-text-secondary)" />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {goals.map((goal) => {
          const isExpanded = expandedGoals.has(goal.id);
          const items = itemsByGoal[goal.id] ?? [];
          return (
            <div
              key={goal.id}
              style={{
                borderRadius: 8,
                background: 'var(--theme-bg-surface)',
                border: '1px solid var(--theme-border-color)',
                overflow: 'hidden',
              }}
            >
              {/* Goal header row */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggleGoal(goal.id)}
                onKeyDown={(e) => e.key === 'Enter' && toggleGoal(goal.id)}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span style={{ fontSize: 14, flexShrink: 0 }}>{isExpanded ? '▾' : '▸'}</span>
                  <Text variant="subtitle1" content={goal.title} />
                </div>

                {/* Status badge + action buttons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <span
                    style={{
                      padding: '2px 10px',
                      borderRadius: 10,
                      background: goal.status === 'active' ? 'var(--theme-color-primary)' : 'var(--theme-border-color)',
                      color: goal.status === 'active' ? '#fff' : 'var(--theme-text-secondary)',
                      fontSize: 11,
                    }}
                  >
                    {goal.status}
                  </span>

                  {/* Edit goal button */}
                  <button
                    data-testid={`edit-goal-btn-${goal.id}`}
                    title="Edit goal"
                    onClick={(e) => openGoalEditDialog(goal, e)}
                    onKeyDown={(e) => e.key === 'Enter' && openGoalEditDialog(goal, e)}
                    style={{
                      ...btnBase,
                      color: 'var(--theme-color-primary)',
                    }}
                  >
                    ✏
                  </button>

                  {/* Archive goal button */}
                  <button
                    data-testid={`archive-goal-btn-${goal.id}`}
                    title="Archive goal"
                    onClick={(e) => openGoalArchiveDialog(goal, e)}
                    onKeyDown={(e) => e.key === 'Enter' && openGoalArchiveDialog(goal, e)}
                    style={{
                      ...btnBase,
                      color: 'var(--theme-text-secondary)',
                    }}
                  >
                    📦
                  </button>
                </div>
              </div>

              {goal.description && (
                <div style={{ paddingLeft: 40, paddingRight: 16, paddingBottom: 8 }}>
                  <Text variant="caption" content={goal.description} customColor="var(--theme-text-secondary)" />
                </div>
              )}

              {isExpanded && (
                <div style={{ borderTop: '1px solid var(--theme-border-color)', padding: '8px 16px 12px' }}>
                  {items.length === 0 ? (
                    <Text variant="caption" content="No items." customColor="var(--theme-text-disabled)" />
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {items.map((item) => (
                        <div
                          key={item.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedItem(item)}
                          onKeyDown={(e) => e.key === 'Enter' && setSelectedItem(item)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 6,
                            border: '1px solid var(--theme-border-color)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            flexWrap: 'wrap',
                          }}
                        >
                          <span
                            style={{
                              padding: '1px 8px',
                              borderRadius: 8,
                              background: STATUS_COLORS[item.status] ?? '#9e9e9e',
                              color: '#fff',
                              fontSize: 10,
                              fontWeight: 600,
                              flexShrink: 0,
                            }}
                          >
                            {item.status}
                          </span>
                          <Text variant="body2" content={item.title} />
                          {item.assignee && (
                            <Text
                              variant="caption"
                              content={item.assignee}
                              customColor="var(--theme-text-secondary)"
                            />
                          )}
                          {item.priority && (
                            <span
                              style={{
                                marginLeft: 'auto',
                                padding: '1px 8px',
                                borderRadius: 8,
                                background: PRIORITY_COLORS[item.priority] ?? '#9e9e9e',
                                color: '#fff',
                                fontSize: 10,
                                flexShrink: 0,
                              }}
                            >
                              {item.priority}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Item detail drawer ─────────────────────────────────────────────── */}
      {selectedItem && (
        <div
          role="dialog"
          aria-label="Item details"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1200,
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'flex-end',
            background: 'rgba(0,0,0,0.4)',
          }}
          onClick={() => setSelectedItem(null)}
        >
          <div
            style={{
              width: 360,
              background: 'var(--theme-bg-surface)',
              borderLeft: '1px solid var(--theme-border-color)',
              padding: 24,
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer header */}
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}
            >
              <Text variant="h6" content="Item Details" />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* Edit item button */}
                <button
                  data-testid="edit-item-btn"
                  onClick={openItemEditDialog}
                  style={{
                    background: 'var(--theme-color-primary)',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: 12,
                    color: '#fff',
                    padding: '4px 12px',
                    fontWeight: 600,
                  }}
                >
                  Edit item
                </button>

                {/* Archive item button */}
                <button
                  data-testid="archive-item-btn"
                  onClick={handleArchiveItem}
                  disabled={isArchivingItem || selectedItem.status === 'done'}
                  title={selectedItem.status === 'done' ? 'Already done' : 'Mark as done'}
                  style={{
                    background: 'none',
                    border: '1px solid var(--theme-border-color)',
                    borderRadius: 4,
                    cursor: isArchivingItem || selectedItem.status === 'done' ? 'not-allowed' : 'pointer',
                    fontSize: 12,
                    color: selectedItem.status === 'done' ? 'var(--theme-text-secondary)' : 'var(--theme-text-primary)',
                    padding: '4px 12px',
                    opacity: isArchivingItem || selectedItem.status === 'done' ? 0.5 : 1,
                  }}
                >
                  {isArchivingItem ? 'Archiving…' : 'Archive'}
                </button>

                {/* Close button */}
                <button
                  onClick={() => setSelectedItem(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 20,
                    color: 'var(--theme-text-secondary)',
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <Text variant="subtitle1" content={selectedItem.title} gutterBottom />
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <span
                style={{
                  padding: '2px 10px',
                  borderRadius: 8,
                  background: STATUS_COLORS[selectedItem.status] ?? '#9e9e9e',
                  color: '#fff',
                  fontSize: 11,
                }}
              >
                {selectedItem.status}
              </span>
              {selectedItem.priority && (
                <span
                  style={{
                    padding: '2px 10px',
                    borderRadius: 8,
                    background: PRIORITY_COLORS[selectedItem.priority] ?? '#9e9e9e',
                    color: '#fff',
                    fontSize: 11,
                  }}
                >
                  {selectedItem.priority}
                </span>
              )}
            </div>
            {selectedItem.assignee && (
              <Text
                variant="body2"
                content={`Assignee: ${selectedItem.assignee}`}
                customColor="var(--theme-text-secondary)"
                gutterBottom
              />
            )}
            {selectedItem.notes ? (
              <Text variant="body2" content={selectedItem.notes} />
            ) : (
              <Text variant="body2" content="No notes available." customColor="var(--theme-text-secondary)" />
            )}
          </div>
        </div>
      )}

      {/* ── Edit item dialog ───────────────────────────────────────────────── */}
      {isItemEditOpen && selectedItem && (
        <div
          role="dialog"
          aria-label="Edit item"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.55)',
          }}
          onClick={closeItemEditDialog}
        >
          <div
            style={{
              width: 440,
              maxWidth: '90vw',
              background: 'var(--theme-bg-surface)',
              borderRadius: 8,
              border: '1px solid var(--theme-border-color)',
              padding: 24,
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text variant="h6" content="Edit Item" />
              <button
                onClick={closeItemEditDialog}
                style={{ ...btnBase, fontSize: 20, color: 'var(--theme-text-secondary)' }}
              >
                ×
              </button>
            </div>

            <div>
              <label
                htmlFor="edit-item-title"
                style={{ display: 'block', fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4 }}
              >
                Title *
              </label>
              <input
                id="edit-item-title"
                data-testid="edit-item-title"
                value={itemEditForm.title}
                onChange={(e) => setItemEditForm((f) => ({ ...f, title: e.target.value }))}
                style={inputStyle}
                autoFocus
              />
            </div>

            <div>
              <label
                htmlFor="edit-item-status"
                style={{ display: 'block', fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4 }}
              >
                Status
              </label>
              <select
                id="edit-item-status"
                data-testid="edit-item-status"
                value={itemEditForm.status}
                onChange={(e) => setItemEditForm((f) => ({ ...f, status: e.target.value as Item['status'] }))}
                style={inputStyle}
              >
                {ITEM_STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="edit-item-priority"
                style={{ display: 'block', fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4 }}
              >
                Priority
              </label>
              <select
                id="edit-item-priority"
                data-testid="edit-item-priority"
                value={itemEditForm.priority}
                onChange={(e) => setItemEditForm((f) => ({ ...f, priority: e.target.value }))}
                style={inputStyle}
              >
                {ITEM_PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>{p || '(none)'}</option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="edit-item-assignee"
                style={{ display: 'block', fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4 }}
              >
                Assignee
              </label>
              <input
                id="edit-item-assignee"
                data-testid="edit-item-assignee"
                value={itemEditForm.assignee}
                onChange={(e) => setItemEditForm((f) => ({ ...f, assignee: e.target.value }))}
                placeholder="e.g. coder"
                style={inputStyle}
              />
            </div>

            <div>
              <label
                htmlFor="edit-item-notes"
                style={{ display: 'block', fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4 }}
              >
                Notes
              </label>
              <textarea
                id="edit-item-notes"
                data-testid="edit-item-notes"
                value={itemEditForm.notes}
                onChange={(e) => setItemEditForm((f) => ({ ...f, notes: e.target.value }))}
                rows={4}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>

            {itemSaveError && (
              <div
                style={{
                  padding: '8px 12px',
                  borderRadius: 4,
                  background: 'rgba(244,67,54,0.08)',
                  border: '1px solid #f44336',
                }}
              >
                <Text variant="caption" content={itemSaveError} customColor="#f44336" />
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={closeItemEditDialog}
                disabled={isItemSaving}
                style={{
                  padding: '6px 16px',
                  borderRadius: 4,
                  border: '1px solid var(--theme-border-color)',
                  background: 'transparent',
                  color: 'var(--theme-text-primary)',
                  cursor: isItemSaving ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleItemEditSave}
                data-testid="edit-item-save"
                disabled={isItemSaving || !itemEditForm.title.trim()}
                style={{
                  padding: '6px 16px',
                  borderRadius: 4,
                  border: 'none',
                  background: 'var(--theme-color-primary)',
                  color: '#fff',
                  cursor: isItemSaving || !itemEditForm.title.trim() ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  opacity: isItemSaving || !itemEditForm.title.trim() ? 0.6 : 1,
                }}
              >
                {isItemSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit goal dialog ───────────────────────────────────────────────── */}
      {isGoalEditOpen && selectedGoal && (
        <div
          role="dialog"
          aria-label="Edit goal"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.55)',
          }}
          onClick={closeGoalEditDialog}
        >
          <div
            style={{
              width: 480,
              maxWidth: '90vw',
              background: 'var(--theme-bg-surface)',
              borderRadius: 8,
              border: '1px solid var(--theme-border-color)',
              padding: 24,
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text variant="h6" content="Edit Goal" />
              <button
                onClick={closeGoalEditDialog}
                style={{ ...btnBase, fontSize: 20, color: 'var(--theme-text-secondary)' }}
              >
                ×
              </button>
            </div>

            <div>
              <label
                htmlFor="edit-goal-title"
                style={{ display: 'block', fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4 }}
              >
                Title *
              </label>
              <input
                id="edit-goal-title"
                data-testid="edit-goal-title"
                value={goalEditForm.title}
                onChange={(e) => setGoalEditForm((f) => ({ ...f, title: e.target.value }))}
                style={inputStyle}
                autoFocus
              />
            </div>

            <div>
              <label
                htmlFor="edit-goal-description"
                style={{ display: 'block', fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4 }}
              >
                Description
              </label>
              <textarea
                id="edit-goal-description"
                data-testid="edit-goal-description"
                value={goalEditForm.description}
                onChange={(e) => setGoalEditForm((f) => ({ ...f, description: e.target.value }))}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>

            <div>
              <label
                htmlFor="edit-goal-status"
                style={{ display: 'block', fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4 }}
              >
                Status
              </label>
              <select
                id="edit-goal-status"
                data-testid="edit-goal-status"
                value={goalEditForm.status}
                onChange={(e) => setGoalEditForm((f) => ({ ...f, status: e.target.value }))}
                style={inputStyle}
              >
                {GOAL_STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {goalSaveError && (
              <div
                style={{
                  padding: '8px 12px',
                  borderRadius: 4,
                  background: 'rgba(244,67,54,0.08)',
                  border: '1px solid #f44336',
                }}
              >
                <Text variant="caption" content={goalSaveError} customColor="#f44336" />
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={closeGoalEditDialog}
                disabled={isGoalSaving}
                style={{
                  padding: '6px 16px',
                  borderRadius: 4,
                  border: '1px solid var(--theme-border-color)',
                  background: 'transparent',
                  color: 'var(--theme-text-primary)',
                  cursor: isGoalSaving ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleGoalEditSave}
                data-testid="edit-goal-save"
                disabled={isGoalSaving || !goalEditForm.title.trim()}
                style={{
                  padding: '6px 16px',
                  borderRadius: 4,
                  border: 'none',
                  background: 'var(--theme-color-primary)',
                  color: '#fff',
                  cursor: isGoalSaving || !goalEditForm.title.trim() ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  opacity: isGoalSaving || !goalEditForm.title.trim() ? 0.6 : 1,
                }}
              >
                {isGoalSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Archive goal confirmation dialog ──────────────────────────────── */}
      {isGoalArchiveOpen && selectedGoal && (
        <div
          role="dialog"
          aria-label="Archive goal"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.55)',
          }}
          onClick={closeGoalArchiveDialog}
        >
          <div
            style={{
              width: 400,
              maxWidth: '90vw',
              background: 'var(--theme-bg-surface)',
              borderRadius: 8,
              border: '1px solid var(--theme-border-color)',
              padding: 24,
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Text variant="h6" content="Archive Goal" />
            <Text
              variant="body2"
              content={`Archive "${selectedGoal.title}"? Choose a final status:`}
              customColor="var(--theme-text-secondary)"
            />

            <div>
              <label
                htmlFor="archive-goal-status"
                style={{ display: 'block', fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4 }}
              >
                Final Status
              </label>
              <select
                id="archive-goal-status"
                data-testid="archive-goal-status"
                value={goalArchiveStatus}
                onChange={(e) => setGoalArchiveStatus(e.target.value as 'done' | 'cancelled')}
                style={inputStyle}
              >
                <option value="done">done — completed successfully</option>
                <option value="cancelled">cancelled — no longer needed</option>
              </select>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={closeGoalArchiveDialog}
                disabled={isGoalArchiving}
                style={{
                  padding: '6px 16px',
                  borderRadius: 4,
                  border: '1px solid var(--theme-border-color)',
                  background: 'transparent',
                  color: 'var(--theme-text-primary)',
                  cursor: isGoalArchiving ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleGoalArchive}
                data-testid="archive-goal-confirm"
                disabled={isGoalArchiving}
                style={{
                  padding: '6px 16px',
                  borderRadius: 4,
                  border: 'none',
                  background: '#757575',
                  color: '#fff',
                  cursor: isGoalArchiving ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  opacity: isGoalArchiving ? 0.6 : 1,
                }}
              >
                {isGoalArchiving ? 'Archiving…' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
